import { ShaderMaterial, GLSL3, Vector2, Vector3, Vector4, type IUniform } from 'three';
import { painterWarpDefines, type SharedUniforms } from './sharedUniforms';
import type { AtmosphereParams } from '../types';
import { hazeFraction } from '../atmosphere';
import { OPEN_WATER } from '../openWater';
import { OPEN_WATER_PATCHES, OPEN_WATER_PLANE, VALUE_NOISE, patchCellM } from '../openWaterPatches';
import { WATER_CUE } from '../waterCues';
import { WAVE_TRAINS, WAVE_VECTORS } from '../waves';
import { WATER_ATTRIBUTES, withProgramAttributes } from './programAttributes';
import type { ShoreUniforms } from './shoreUniforms';
import { WATER_COVER } from '../waterCover';
import { createWaterCoverUniforms, type WaterCoverUniforms } from './waterCoverUniforms';

import waterVert from '../shaders/water.vert?raw';
import waterFrag from '../shaders/water.frag?raw';

/*
 * The painter camera sees the water between 2.7 and 3.6 km (medians: IJ 2.85 km, canals 3.18 km,
 * rivers 3.58 km, measured on a painter-view render with each kind marked). 3.2 km splits the
 * difference, so every kind lands within half an L* of its palette median there.
 */
export const PAINTER_WATER_DISTANCE_M = 3200;

type FogParams = Pick<
  AtmosphereParams,
  | 'density'
  | 'exponent'
  | 'heightFalloff'
  | 'maxOpacity'
  | 'calibrationDistanceM'
  | 'painterExponent'
>;

/*
 * Each water kind's vertex colour is its palette median (STYLE §7), which the painting shows at
 * the painter's distance. water.frag takes the haze the view's own fog adds there back out of the
 * colour, so each fog gives the median back at that distance: the painter view's profile adds a
 * fifth of what exp² adds (STYLE §3.2), and taking that out in walk and fly views too would
 * lighten their water by up to 2 L*.
 */
export function painterWaterHaze(fog: FogParams): number {
  return hazeFraction({ ...fog, hazeColor: [0, 0, 0] }, PAINTER_WATER_DISTANCE_M, 0, 1);
}

export function freeWaterHaze(fog: FogParams): number {
  return hazeFraction({ ...fog, hazeColor: [0, 0, 0] }, PAINTER_WATER_DISTANCE_M, 0, 0);
}

function fogFromShared(shared: SharedUniforms): FogParams {
  return {
    density: shared.uFogDensity.value,
    exponent: shared.uFogExponent.value,
    heightFalloff: shared.uFogHeightFalloff.value,
    maxOpacity: shared.uFogMaxOpacity.value,
    calibrationDistanceM: shared.uFogCalibrationM.value,
    painterExponent: shared.uFogPainterExponent.value,
  };
}

/* openWater.glsl's uniforms: the painter's open water, its plane and its patches (STYLE §7). */
export function openWaterUniforms(): Record<string, IUniform> {
  const plane = OPEN_WATER_PLANE;
  const { spread, abCorrelation } = OPEN_WATER_PATCHES;
  const perNoise = (c: 0 | 1 | 2): number => spread[c] / VALUE_NOISE.robustSd;
  return {
    uOpenWaterFreeScale: { value: OPEN_WATER.freeBaseScale },
    uOpenWaterKindLimit: { value: OPEN_WATER.kindLimit },
    uOpenWaterSteep: { value: new Vector3(...OPEN_WATER.steep) },
    uOpenWaterShallow: { value: new Vector3(...OPEN_WATER.shallow) },
    uOpenWaterRamp: { value: new Vector2(OPEN_WATER.shallowDeg, OPEN_WATER.steepDeg) },
    uOpenWaterPlaneCentre: { value: new Vector2(...plane.centreM) },
    uOpenWaterPlaneRange: { value: new Vector4(...plane.rangeXM, ...plane.rangeYM) },
    uOpenWaterPlaneSlope: { value: new Vector4(...plane.lightnessPerM, ...plane.bPerM) },
    uOpenWaterPatchCells: { value: new Vector3(patchCellM(0), patchCellM(1), patchCellM(2)) },
    uOpenWaterPatchSpread: {
      value: new Vector4(perNoise(0), perNoise(1), perNoise(2), abCorrelation),
    },
  };
}

export function createWaterMaterial(
  sharedUniforms: SharedUniforms,
  shoreUniforms: ShoreUniforms,
  coverUniforms: WaterCoverUniforms = createWaterCoverUniforms()
): ShaderMaterial {
  const material = new ShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: waterVert,
    fragmentShader: waterFrag,
    defines: {
      ...painterWarpDefines(sharedUniforms),
      WAVE_TRAINS,
      WATER_COVERS: WATER_COVER.slots,
    },
    uniforms: {
      ...sharedUniforms,
      ...shoreUniforms,
      ...coverUniforms,
      uWaterPainterHaze: { value: painterWaterHaze(fogFromShared(sharedUniforms)) },
      uWaterFreeHaze: { value: freeWaterHaze(fogFromShared(sharedUniforms)) },
      ...openWaterUniforms(),
      uWaveVector: {
        value: WAVE_VECTORS.map((w) => new Vector4(w.kx, w.kz, w.omega, w.phase)),
      },
      uWaveAmplitude: { value: WAVE_VECTORS.map((w) => w.amplitudeM) },
      uWaterCue: {
        value: new Vector3(
          WATER_CUE.normalReflectance,
          WATER_CUE.rippleCellM,
          WATER_CUE.rippleGain
        ),
      },
    },
    vertexColors: true,
    depthWrite: true,
    depthTest: true,
  });

  material.userData['ams'] = true;

  return withProgramAttributes(material, WATER_ATTRIBUTES);
}
